import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useActionItemToggleCompleteMutation } from '../../../../../../../../queries';
import { styleSheet } from './styles';

interface IProps {
	actionItem: Api.ActionItemViewModel;
	onClick?: () => void;
	onCheckChanged?: (checked: boolean) => void;
}

export const ActionItemCheckboxIcon = ({ actionItem, onClick, onCheckChanged }: IProps) => {
	// Use the actual actionItem state as the source of truth, with local override during API calls
	const [isTogglingState, setIsTogglingState] = useState<boolean | null>(null);

	// Use local toggling state if we're in the middle of an API call, otherwise use actual actionItem state
	const displayCompleted = isTogglingState !== null ? isTogglingState : actionItem.isCompleted || false;
	const canToggle = !!actionItem.id && !actionItem.isBusy;

	// Overdue logic from the old ActionItemFeedCard
	const isOverdue = useMemo(() => {
		return actionItem.dueDate ? moment(actionItem.dueDate).isBefore(moment()) && !actionItem.isCompleted : false;
	}, [actionItem.dueDate, actionItem.isCompleted]);

	const toggleCompleteMutation = useActionItemToggleCompleteMutation({
		onSuccess: (data: Api.IActionItem, variables: { actionItemId: string; isCompleted: boolean }) => {
			// Reset local state once API call succeeds
			setIsTogglingState(null);

			// Call the callback if provided
			if (onCheckChanged) {
				onCheckChanged(variables.isCompleted);
			}
			// Show success feedback
			toast.success(variables.isCompleted ? 'Action item completed' : 'Action item marked as in progress');
		},
		onError: (error: Api.IOperationResultNoValue) => {
			// Reset local state on error
			setIsTogglingState(null);
			// Show error feedback
			toast.error('Failed to update action item status');
			console.error('Toggle action item error:', error);
		},
	});

	const handleClick = () => {
		if (!canToggle || toggleCompleteMutation.isPending) return;

		const newIsCompleted = !actionItem.isCompleted;

		// Optimistically update the UI
		setIsTogglingState(newIsCompleted);

		// Make the API call using the structured mutation
		toggleCompleteMutation.mutate({
			actionItemId: actionItem.id,
			isCompleted: newIsCompleted,
		});

		// Call the onClick callback if provided
		if (onClick) {
			onClick();
		}
	};

	return (
		<div
			className={css(
				styleSheet.checkboxContainer,
				displayCompleted && styleSheet.checkboxCompleted,
				isOverdue && styleSheet.checkboxOverdue,
				(!canToggle || toggleCompleteMutation.isPending) && styleSheet.checkboxDisabled
			)}
			onClick={handleClick}
			title={
				!canToggle
					? 'Cannot modify this action item'
					: toggleCompleteMutation.isPending
						? 'Updating...'
						: isOverdue && !displayCompleted
							? 'This action item is overdue'
							: displayCompleted
								? 'Mark as incomplete'
								: 'Mark as complete'
			}
			style={{ cursor: canToggle && !toggleCompleteMutation.isPending ? 'pointer' : 'not-allowed' }}
		>
			{displayCompleted && (
				<span className={css(styleSheet.checkmark, isOverdue && styleSheet.checkmarkOverdue)}>✓</span>
			)}
		</div>
	);
};
